import { Head, useForm, Link } from "@inertiajs/react"
import { useMemo, useState } from "react"
import { Send, ArrowLeft, AlertTriangle, Users } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function BroadcastNew({ currentUser, audience = {}, mailingAddressSet, pendingCount }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const form = useForm({
    subject: "",
    body: "",
    audience_type: audience.type || "selection",
    role: audience.role || "",
    status: audience.status || "",
    account_ids: audience.accountIds || [],
  })

  // Warn about any {{token}} that isn't the supported {{first_name}} — it will
  // be sent literally (KTD7).
  const tokenWarnings = useMemo(() => {
    const matches = form.data.body.match(/\{\{[^}]*\}\}/g) || []
    return [...new Set(matches.filter((t) => !/^\{\{\s*first_name\s*\}\}$/i.test(t)))]
  }, [form.data.body])

  const eligibleCount = audience.eligibleCount || 0
  const canSend = mailingAddressSet && eligibleCount > 0 && !form.processing

  const submit = () => {
    setConfirmOpen(false)
    form.post("/app/admin/broadcasts", { preserveScroll: true })
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="New Broadcast - Kingdom Factor Admin" />

      <div className="space-y-6 max-w-3xl">
        <div>
          <Link
            href="/app/admin/broadcasts"
            className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-800"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Broadcasts
          </Link>
          <h1 className="text-2xl font-bold text-zinc-900 mt-2">Compose Broadcast</h1>
          <p className="text-zinc-500">A plain-text email sent individually to your chosen audience.</p>
        </div>

        {/* Audience summary */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-kf-red" />
              <div>
                <p className="font-medium text-zinc-900">{audience.label || "Selected recipients"}</p>
                <p className="text-sm text-zinc-500">
                  {eligibleCount} eligible recipient{eligibleCount === 1 ? "" : "s"} after opt-out exclusions
                </p>
              </div>
            </div>
            <Badge variant="outline" className="border-zinc-300 text-zinc-600">{audience.type}</Badge>
          </CardContent>
        </Card>

        {!mailingAddressSet && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <div>
              A physical mailing address is required by law (CAN-SPAM) before sending broadcasts.{" "}
              <Link href="/app/admin/settings" className="underline font-medium">Set it in Settings</Link>.
            </div>
          </div>
        )}

        {form.errors.base && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700">
            {form.errors.base}
          </div>
        )}

        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <CardTitle className="text-lg font-display">Message</CardTitle>
            <CardDescription>
              Use{" "}
              <code className="px-1 py-0.5 rounded bg-zinc-100 text-zinc-700 font-mono text-xs">{"{{first_name}}"}</code>{" "}
              to greet each recipient by name.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={form.data.subject}
                onChange={(e) => form.setData("subject", e.target.value)}
                placeholder="A note from Kingdom Factor"
              />
              {form.errors.subject && <p className="text-sm text-red-600">{form.errors.subject}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="body">Message</Label>
              <Textarea
                id="body"
                rows={12}
                value={form.data.body}
                onChange={(e) => form.setData("body", e.target.value)}
                placeholder={"Hi {{first_name}},\n\n"}
              />
              {form.errors.body && <p className="text-sm text-red-600">{form.errors.body}</p>}
              {tokenWarnings.length > 0 && (
                <p className="text-sm text-amber-600">
                  Unrecognized token{tokenWarnings.length === 1 ? "" : "s"} will be sent as-is:{" "}
                  {tokenWarnings.join(", ")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-4">
          <Button variant="gradient" disabled={!canSend} onClick={() => setConfirmOpen(true)}>
            <Send className="h-4 w-4 mr-2" />
            {form.processing ? "Sending..." : `Send to ${eligibleCount}`}
          </Button>
          <Link href="/app/admin/people" className="text-sm text-zinc-500 hover:text-zinc-800">
            Cancel
          </Link>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Send this broadcast?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email <strong>{eligibleCount}</strong> recipient{eligibleCount === 1 ? "" : "s"}.
              Each one gets an individual email — they won't see each other. Delivery runs in the background.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submit} className="bg-kf-red hover:bg-red-700 text-white">
              Send broadcast
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}
